import * as React from 'react';
import { Text } from '@/hen-ui/atomics/Text';
import { Button } from '@/hen-ui/atomics/Button';
import { Flex } from '@/hen-ui/composites/Flex';
import type { TextCellValue } from '../../types';
import { cn } from '@/hen-ui/utils';

interface TextCellProps {
  value: TextCellValue | undefined;
  /** When `false`, skip line-clamp / Read more. Defaults to `true`. */
  clamp?: boolean;
  /**
   * When `true`, render `value.text` via `dangerouslySetInnerHTML`.
   * Defaults to `false`.
   *
   * **Call-site invariant:** HTML must already be sanitized (allowlist) by
   * the consumer before passing it here. hen-ui does not sanitize markup —
   * it is UI-only. Use DE `sanitizeHtmlString` (or equivalent) upstream.
   */
  html?: boolean;
}

export const TextCell: React.FC<TextCellProps> = ({
  value,
  clamp = true,
  html = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [needsClamp, setNeedsClamp] = React.useState(false);
  const textRef = React.useRef<HTMLElement>(null);
  // Ref mirrors isExpanded so checkOverflow can read the current value
  const isExpandedRef = React.useRef(false);

  const checkOverflow = React.useCallback(() => {
    if (!clamp) return;
    // Skip measurement while expanded — the text element has no overflow when
    // line-clamp-3 is removed, which would incorrectly hide the "Read less" button.
    if (textRef.current && !isExpandedRef.current) {
      setNeedsClamp(
        textRef.current.scrollHeight > textRef.current.clientHeight,
      );
    }
  }, [clamp]);

  const handleToggle = React.useCallback(() => {
    const next = !isExpandedRef.current;
    isExpandedRef.current = next;
    setIsExpanded(next);
  }, []);

  React.useEffect(() => {
    isExpandedRef.current = false;
    setIsExpanded(false);
    setNeedsClamp(false);
    checkOverflow();
  }, [value, checkOverflow]);

  React.useEffect(() => {
    if (!clamp) return;
    const el = textRef.current;
    if (!el) return;
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkOverflow, clamp]);

  if (!value?.text) {
    console.error(
      '[TextCell] No text provided. TextCell requires a text value.',
    );
    return null;
  }

  const clampClassName = cn(clamp && !isExpanded && 'line-clamp-3');

  return (
    <Flex direction='column' gap='xxs' align='start'>
      {html ? (
        <Text
          ref={textRef}
          // Block tags are common in partner TCPA HTML; prefer `div` when html.
          tag='div'
          variant='b5'
          color='subtext'
          className={clampClassName}
          dangerouslySetInnerHTML={{ __html: value.text }}
        >
          {null}
        </Text>
      ) : (
        <Text
          ref={textRef}
          tag='span'
          variant='b5'
          color='subtext'
          className={clampClassName}
        >
          {value.text}
        </Text>
      )}
      {clamp && needsClamp && (
        <Button
          type='button'
          variant='textOnlyNeutral'
          onClick={handleToggle}
          className='underline decoration-solid h-auto font-bold text-l1 leading-l1'
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </Button>
      )}
    </Flex>
  );
};

TextCell.displayName = 'TextCell';
